import { ElementProps, Combobox, useCombobox, ComboboxProps, Stack, Group } from '@mantine/core';
import { useUncontrolled } from '@mantine/hooks';
import { Button } from '../button/Button';
import { IconCaretDownFilled, IconCaretUpFilled } from '@tabler/icons-react';
import { Badge } from '../badge/Badge';
import { ReactNode } from 'react';
import menuStyles from '../styles/DropdownMenu.module.css';

export interface BaseFilterButtonProps<T> {
    label: string;
    disabled?: boolean;
    value?: T;
    defaultValue?: T;
    submittedValue?: T;
    onChange?: (value: T) => void;
    onSubmit?: (value: T) => void;
    size?: string;
    showSubmitButtons?: boolean;
    comboboxProps?: Partial<ComboboxProps>;
    children: ReactNode;
    hasActiveFilters: boolean;
    dropdownClassName?: string;
    triggerClassName?: string;
    badgeClassName?: string;
}

export interface BaseFilterButtonHooks<T> {
    value: T;
    setValue: (value: T) => void;
    submittedValue: T;
    setSubmittedValue: (value: T) => void;
    combobox: ReturnType<typeof useCombobox>;
}

export function useBaseFilterButton<T>({
    value,
    defaultValue,
    submittedValue,
    onChange,
    onSubmit,
    finalValue,
}: {
    value?: T;
    defaultValue?: T;
    submittedValue?: T;
    onChange?: (value: T) => void;
    onSubmit?: (value: T) => void;
    finalValue: T;
}): BaseFilterButtonHooks<T> {
    const combobox = useCombobox({
        onDropdownClose: () => combobox.resetSelectedOption(),
        onDropdownOpen: () => combobox.updateSelectedOptionIndex('active'),
    });

    const [_value, setValue] = useUncontrolled({
        value,
        defaultValue,
        finalValue,
        onChange,
    });

    const [_submittedValue, setSubmittedValue] = useUncontrolled<T>({
        value: submittedValue,
        defaultValue: finalValue,
        finalValue,
        onChange: onSubmit,
    });

    return {
        value: _value,
        setValue,
        submittedValue: _submittedValue,
        setSubmittedValue,
        combobox,
    };
}

export function BaseFilterButton<T>({
    label,
    disabled,
    size,
    showSubmitButtons = false,
    comboboxProps,
    children,
    hasActiveFilters,
    dropdownClassName,
    triggerClassName,
    badgeClassName,
    hooks,
    hiddenInputs,
    ...props
}: BaseFilterButtonProps<T> &
    ElementProps<'div', 'onChange'> & {
        hooks: BaseFilterButtonHooks<T>;
        hiddenInputs?: ReactNode;
    }) {
    const { value, setValue, submittedValue, setSubmittedValue, combobox } = hooks;

    return (
        <Combobox
            store={combobox}
            size={size}
            classNames={{ dropdown: `${menuStyles.dropdown} ${dropdownClassName || ''}` }}
            aria-label={label}
            {...comboboxProps}
            {...props}
        >
            <Combobox.Target>
                <div className={triggerClassName}>
                    <Button
                        size={size}
                        disabled={disabled}
                        appearance="outline"
                        intent={hasActiveFilters || combobox.dropdownOpened ? 'prominent' : 'neutral'}
                        rightSection={
                            combobox.dropdownOpened ? (
                                <IconCaretUpFilled size="1rem" />
                            ) : (
                                <IconCaretDownFilled size="1rem" />
                            )
                        }
                        onClick={() => combobox.toggleDropdown()}
                        styles={{ label: { overflow: 'visible' }, root: { overflow: 'visible' } }}
                    >
                        {label}
                        {hasActiveFilters && (
                            <Badge
                                type="dot"
                                status="prominent"
                                className={badgeClassName}
                                data-testid="status-dot"
                            />
                        )}
                    </Button>
                </div>
            </Combobox.Target>

            <Combobox.Dropdown data-composed>
                <Stack p="sm" gap="sm">
                    {children}

                    {showSubmitButtons && (
                        <Group justify="flex-end" gap="sm" wrap="nowrap">
                            <Button
                                appearance="outline"
                                size="compact-sm"
                                onClick={() => {
                                    setValue(submittedValue);
                                    combobox.closeDropdown();
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                size="compact-sm"
                                onClick={() => {
                                    setSubmittedValue(value);
                                    combobox.closeDropdown();
                                }}
                            >
                                Apply
                            </Button>
                        </Group>
                    )}
                </Stack>
            </Combobox.Dropdown>

            {hiddenInputs}
        </Combobox>
    );
}
